import { isDefined } from "@/utils";
import { Speech } from "@/entities/Speech";

export class SpeechContainer {

  private _startLetters: string[] = [];
  private _speechMap: Record<string, Speech[]> = {};
  private _speechesAfterSearchAndFilter: Speech[] = [];
  private readonly _speeches: Speech[] = [];
  private readonly _sortRegex = new RegExp('[A-Z]');

  constructor(entities: Speech[]) {
    this.createSpeechMap(entities);
    this._speechesAfterSearchAndFilter = entities;
    this._speeches = entities;
    this.createStartLetters(entities);
  }

  private createStartLetters(entities: Speech[]): void {
    const letterSet = new Set<string>();
    for (const speech of entities) {
      let firstChar = speech.sortOrder[0].toUpperCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
      if (!this._sortRegex.test(firstChar)) {
        firstChar = '#';
      }
      letterSet.add(firstChar);
    }
    this._startLetters = [...letterSet];
  }

  private createSpeechMap(entities: Speech[]): void {
    this._speechMap = {}
    for (const speech of entities) {
      let date = speech.startDate;
      if (!isDefined(this._speechMap[date])) {
        this._speechMap[date] = [];
      }
      this._speechMap[date].push(speech);
    }
  }

  public getSpeechMap(): Record<string, Speech[]> {
    return this._speechMap;
  }

  public hasMorePages(pageSize): boolean {
    return this._speechMap.length > pageSize;
  }

  public filterSpeeches(searchValue: string, filters: Record<string, string>, startLetter: string): void {
    let filteredSpeeches: Speech[];
    searchValue = searchValue.trim().toLowerCase();
    if (searchValue.length > 0) {
      filteredSpeeches = this.applyFilters(filters, this._speeches);
      filteredSpeeches = filteredSpeeches.filter(e => {
        return e.hasSearchValue(searchValue);
      });
      filteredSpeeches = this.applyFilters(filters, filteredSpeeches);
    } else {
      filteredSpeeches = this.applyFilters(filters, this._speeches);
    }
    this._speechesAfterSearchAndFilter = filteredSpeeches;
    if (startLetter !== 'all' && startLetter !== 'favorites') {
      filteredSpeeches = filteredSpeeches.filter(speech => {
        let firstChar = speech.sortOrder[0].toUpperCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
        if (!this._sortRegex.test(firstChar)) {
          firstChar = '#';
        }
        return firstChar === startLetter;
      })
    } else if (startLetter === 'favorites') {
      let favoritesString = window.localStorage.getItem('egcf_favorite_program_list');
      if (isDefined(favoritesString)) {
        const favoritesIds: string[] = JSON.parse(favoritesString)
        filteredSpeeches = filteredSpeeches.filter(speech => {
          return favoritesIds.includes(speech.id);
        });
      } else {
        filteredSpeeches = [];
      }
    }
    this.createSpeechMap(filteredSpeeches);
  }

  private applyFilters(filters: Record<string, string>, speeches: Speech[]): Speech[] {
    if (isDefined(filters) && Object.entries(filters).length > 0) {
      for (const [key, value] of Object.entries(filters)) {
        speeches = speeches.filter(e => {
          if (isDefined(e.themes)) {
            return e.themes.indexOf(value) > -1;
          }
          return false;
        });
      }
    }
    return speeches;
  }

  public hasEntries(): boolean {
    return JSON.stringify(this._speechMap) !== JSON.stringify({});
  }


  get startLetters(): string[] {
    return this._startLetters;
  }

  public letterExistsInCurrentList(letter: string): boolean {
    for (const speech of this._speechesAfterSearchAndFilter) {
      let firstChar = speech.sortOrder[0].toUpperCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
      if (!this._sortRegex.test(firstChar)) {
        firstChar = '#';
      }
      if (firstChar === letter) {
        return true;
      }
    }
    return false;
  }

  get speeches(): Speech[] {
    return this._speeches;
  }

  getSpeechesForSpeakerById(speakerId): Speech[] {
    if (!isDefined(this._speeches) || this._speeches.length === 0) {
      return [];
    }
    return this._speeches.filter(s => s.persons.find(e => e.id === speakerId));
  }
}
